import * as React from "react";
import { cn } from "./cn.js";
import { Button } from "./button.js";

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div aria-hidden="true" className={cn("animate-pulse rounded bg-muted", className)} {...props} />;
}

export interface LoadingStateProps {
  /** What is loading, e.g. "invoices". Announced to screen readers. */
  label: string;
  rows?: number;
  className?: string;
}

/**
 * The loading state is a shaped skeleton, not a spinner, so the page does not
 * jump when data arrives.
 */
export function LoadingState({ label, rows = 5, className }: LoadingStateProps) {
  return (
    <div className={cn("space-y-2 p-1", className)}>
      <p role="status" className="sr-only">
        Loading {label}…
      </p>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-10 w-full" style={{ opacity: 1 - index * 0.12 }} />
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

/**
 * "Nothing here yet" — deliberately calm. An empty result must never be
 * mistaken for a failure, so this shares no styling with <ErrorState>.
 */
export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border",
        "bg-muted/30 px-6 py-12 text-center",
        className,
      )}
    >
      {icon ? (
        <span aria-hidden="true" className="text-muted-foreground [&_svg]:size-8">
          {icon}
        </span>
      ) : null}
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        {description ? (
          <div className="mx-auto max-w-md text-sm text-muted-foreground">{description}</div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export interface ErrorStateProps {
  title?: string;
  /** The failure detail — an API error message, a network failure, etc. */
  message: string;
  /** Stable machine-readable code from the API error taxonomy, when there is one. */
  code?: string | null;
  onRetry?: () => void;
  className?: string;
}

/**
 * "Something broke" — loud, red-bordered, with a retry. Visually distinct from
 * <EmptyState> by construction (solid destructive border vs dashed muted).
 */
export function ErrorState({
  title = "Could not load this data",
  message,
  code,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-start gap-3 rounded-lg border-2 border-destructive/60",
        "bg-destructive/10 px-5 py-4",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-destructive text-xs font-bold text-destructive-foreground"
        >
          !
        </span>
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-sm text-muted-foreground">{message}</p>
          {code ? (
            <p className="font-mono text-xs text-muted-foreground">
              <span className="sr-only">Error code: </span>
              {code}
            </p>
          ) : null}
        </div>
      </div>
      {onRetry ? (
        <Button size="sm" variant="outline" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}
